import React, { useEffect, useState } from 'react';
import { walletServices } from '../services/WalletServices';
import { toast } from 'react-toastify';
import DataTable from 'react-data-table-component';
import {MEDIA_URL} from '../global_constants';

const Topup = () => {
    const [walletList, setWalletList] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [formData, setFormData] = useState({
        network: '',
        amount: ''
    });
    const [walletAddress, setWalletAddress] = useState('');
    const [showWallet, setShowWallet] = useState(false);
    const [proofType, setProofType] = useState('');
    const [screenshot, setScreenshot] = useState(null);
    const [txnHash, setTxnHash] = useState('');
    const [topupTransactions, setTopupTransactions] = useState([]);

    const [page, setPage] = useState(1);
    const [limit] = useState(10);
    const [totalPages, setTotalPages] = useState(1);

    const userId = localStorage.getItem('id');

    useEffect(() => {
        getActiveWalletDetails();
        getTopupTransactions();
    }, [page]);

    const getActiveWalletDetails = async () => {
        try {
            const response = await walletServices.getActiveWallet();
            const wallets = response.data.data;
            setWalletList(wallets);
        } catch (error) {
            toast.error('Error fetching wallet details');
        } finally {
            setIsLoading(false);
        }
    };

    const getTopupTransactions = async () => {
        try {
            const response = await walletServices.getTopupTransactions(userId, page, limit);
            if(response.status === 200){
                const { data, pagination } = response.data;
                setTopupTransactions(data);
                setTotalPages(pagination.totalPages);
            } else {
                toast.error("Something went wrong!");
            }
        } catch (error) {
            console.error('Error fetching topup transactions details');
        } finally {
            setIsLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const { network, amount } = formData;

        if (network && amount > 0) {
            const selectedWallet = walletList.find(
                (wallet) => wallet.network_type.toLowerCase() === network.toLowerCase()
            );

            if (selectedWallet) {
                setWalletAddress(selectedWallet.wallet_address);
                setShowWallet(true);
            } else {
                toast.error('Selected network not available.');
                setShowWallet(false);
            }
        } else {
            alert('Please select a network and enter a valid amount.');
        }
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(walletAddress);
        toast.success('Wallet address copied to clipboard!');
    };

    const handleProofTypeChange = (e) => {
        setProofType(e.target.value);
        setScreenshot(null);
        setTxnHash('');
    };

    const handleScreenshotUpload = (e) => {
        setScreenshot(e.target.files[0]);
    };

    const handleFinalSubmit = async (e) => {
        e.preventDefault();

        if (proofType === 'screenshot' && !screenshot) {
            alert('Please upload a transaction screenshot.');
            return;
        }

        if (proofType === 'txnHash' && txnHash.trim() === '') {
            alert('Please enter the transaction hash ID.');
            return;
        }

        const formDataToSend = new FormData();
        formDataToSend.append('user_id', userId);
        formDataToSend.append('deposit_wallet_address', walletAddress);
        formDataToSend.append('network', formData.network);
        formDataToSend.append('amount', formData.amount);
        formDataToSend.append('status', 'pending');

        if (proofType === 'txnHash') {
            formDataToSend.append('transaction_hash', txnHash.trim());
        }

        if (proofType === 'screenshot') {
            formDataToSend.append('transaction_image', screenshot);
        }

        try {
            const res = await walletServices.topupWallet(formDataToSend);
            toast.success('Top-up submitted successfully! It will take upto 24 Hrs to confirm.');
            getTopupTransactions();
            // Reset form after successful submission
            setShowWallet(false);
            setFormData({ network: '', amount: '' });
            setProofType('');
            setScreenshot(null);
            setTxnHash('');
            setWalletAddress('');
        } catch (err) {
            console.error(err);
            toast.error(err?.response?.data?.message || 'Submission failed');
        }
    };

    const handleBackToForm = () => {
        setShowWallet(false);
        setProofType('');
        setScreenshot(null);
        setTxnHash('');
    };

    const columns = [
        {
            name: "Date",
            selector: row => new Date(row.createdAt).toLocaleString(),
            sortable: true,
        },
        {
            name: "Proof",
            cell: row => {
                if (row.transaction_hash && row.transaction_hash.trim() !== '') {
                    return (
                        <span 
                            className="text-primary text-decoration-underline" 
                            style={{ cursor: 'pointer', fontSize: '12px' }}
                            title={row.transaction_hash}
                        >
                            {row.transaction_hash.length > 20 
                                ? `${row.transaction_hash.substring(0, 20)}...` 
                                : row.transaction_hash
                            }
                        </span>
                    );
                } else if (row.transaction_image && row.transaction_image.trim() !== '') {
                    return (
                        <span 
                            className="text-success" 
                            style={{ cursor: 'pointer' }}
                            onClick={() => window.open(`${MEDIA_URL}/uploads/${row.transaction_image}`, '_blank')}
                            title="Click to view transaction image"
                        >
                            🖼️ Image
                        </span>
                    );
                } else {
                    return <span className="text-muted">N/A</span>;
                }
            },
            sortable: false,
        },
        {
            name: "Network",
            selector: row => row.network,
            sortable: true,
        },
        {
            name: "Amount",
            selector: row => row.amount,
            sortable: true,
            right: true,
        },
        {
            name: "Status",
            cell: row => (
                <span
                    className={`badge ${
                        row.status === "pending" ? "bg-warning" : "bg-success"
                    }`}
                >
                    {row.status}
                </span>
            ),
            sortable: true,
        },
    ];

    return (
        <>
            <div id="cryptoon-layout" className="theme-orange">
                <div className="col-xl-12 col-xxl-12 px-2">
                    <div className="card">
                        <div className="card-header py-3 d-flex justify-content-between bg-transparent border-bottom align-items-center flex-wrap">
                            <h6 className="mb-0 fw-bold">Deposit</h6>
                            {showWallet && (
                                <button 
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={handleBackToForm}
                                >
                                    Back to Form
                                </button>
                            )}
                        </div>
                        <div className="card-body">
                            {!showWallet ? (
                                // Initial deposit form
                                <form onSubmit={handleSubmit}>
                                    <div className="row">
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label htmlFor="network" className="form-label">Select Network</label>
                                                <select
                                                    className="form-select"
                                                    id="network"
                                                    name="network"
                                                    value={formData.network}
                                                    onChange={handleChange}
                                                    required
                                                >
                                                    <option value="" disabled>Select a network</option>
                                                    <option value="USDT_ERC20">USDT ERC20</option>
                                                    <option value="USDT_TRC20">USDT TRC20</option>
                                                    <option value="USDT_BEP20">USDT BEP20</option>
                                                    <option value="BTC">BTC</option>
                                                    <option value="ETH">ETH</option>
                                                </select>
                                            </div>
                                        </div>
                                        <div className="col-md-6">
                                            <div className="mb-3">
                                                <label htmlFor="amount" className="form-label">Amount to Top-up</label>
                                                <input
                                                    type="number"
                                                    className="form-control"
                                                    id="amount"
                                                    name="amount"
                                                    placeholder="Enter amount"
                                                    min="0"
                                                    step="any"
                                                    value={formData.amount}
                                                    onChange={handleChange}
                                                    required
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="d-grid mb-3">
                                        <button type="submit" className="btn flex-fill btn-light-warning py-2 fs-5 text-uppercase px-5">
                                            Proceed to Deposit
                                        </button>
                                    </div>
                                </form>
                            ) : (
                                // Wallet address and proof submission form
                                <div>
                                    <div className="mb-4">
                                        <div className="alert alert-info">
                                            <h6 className="fw-bold mb-2">Deposit Details</h6>
                                            <p className="mb-1"><strong>Network:</strong> {formData.network}</p>
                                            <p className="mb-0"><strong>Amount:</strong> {formData.amount}</p>
                                        </div>
                                    </div>

                                    <div className="mb-4">
                                        <label htmlFor="walletAddress" className="form-label">Deposit Wallet Address</label>
                                        <div className="input-group">
                                            <input
                                                type="text"
                                                id="walletAddress"
                                                className="form-control"
                                                value={walletAddress}
                                                readOnly
                                            />
                                            <button
                                                className="btn btn-outline-secondary"
                                                type="button"
                                                onClick={handleCopy}
                                            >
                                                Copy
                                            </button>
                                        </div>
                                        <div className="form-text">Send the exact amount to the above address.</div>
                                    </div>

                                    <form onSubmit={handleFinalSubmit}>
                                        <div className="mb-3">
                                            <label className="form-label">Proof of Transaction</label>
                                            <select
                                                className="form-select"
                                                value={proofType}
                                                onChange={handleProofTypeChange}
                                                required
                                            >
                                                <option value="" disabled>Select proof type</option>
                                                <option value="screenshot">Upload Screenshot</option>
                                                <option value="txnHash">Enter Transaction Hash ID</option>
                                            </select>
                                        </div>

                                        {proofType === 'screenshot' && (
                                            <div className="mb-3">
                                                <label className="form-label">Upload Transaction Screenshot</label>
                                                <input
                                                    type="file"
                                                    className="form-control"
                                                    accept="image/*"
                                                    onChange={handleScreenshotUpload}
                                                />
                                            </div>
                                        )}

                                        {proofType === 'txnHash' && (
                                            <div className="mb-3">
                                                <label className="form-label">Transaction Hash ID</label>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    value={txnHash}
                                                    onChange={(e) => setTxnHash(e.target.value)}
                                                    placeholder="Enter hash ID"
                                                />
                                            </div>
                                        )}

                                        <div className="d-grid">
                                            <button type="submit" className="btn btn-light-success text-uppercase">
                                                Submit Transaction Proof
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            
            <div className="row mt-3">
                <div className="col-xl-12">
                    <div className="card no-bg">
                        <div className="card-header py-3">
                            <h6 className="mb-0 fw-bold">Transaction History</h6>
                        </div>
                        <div className="card-body">
                            <DataTable
                                columns={columns}
                                data={topupTransactions}
                                pagination
                                highlightOnHover
                                responsive
                                persistTableHead
                                noDataComponent="No Transactions Found"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Topup;